import {
  type InfiniteData,
  type QueryKey,
  useInfiniteQuery,
} from "@tanstack/react-query";
import {
  type EntityListOfTransactionItem,
  getTransactions,
} from "Infrastructure/Api/Api";
import { processResponseWithApiProblemDetails } from "Infrastructure/Api/ApiUtils";

const TRANSACTIONS_QUERY_KEY = "TRANSACTIONS";

export const transactionsKeys = {
  all: [TRANSACTIONS_QUERY_KEY],
  byParams: (limit: number, nextPageLimit: number) => [
    ...transactionsKeys.all,
    limit,
    nextPageLimit,
  ],
};

type Props = {
  limit?: number;
  nextPageLimit?: number;
};

type PageParams = {
  offset: number;
  limit: number;
};

export const useTransactions = ({
  limit = 10,
  nextPageLimit = 50,
}: Props = {}) =>
  useInfiniteQuery<
    EntityListOfTransactionItem,
    unknown,
    InfiniteData<EntityListOfTransactionItem>,
    QueryKey,
    PageParams
  >({
    queryKey: transactionsKeys.byParams(limit, nextPageLimit),
    queryFn: processResponseWithApiProblemDetails(
      ({ pageParam: { limit, offset } }) => getTransactions(offset, limit),
    ),
    initialPageParam: { offset: 0, limit },
    getNextPageParam: (lastPage: EntityListOfTransactionItem | null) => {
      if (!lastPage) {
        return undefined; //somehow we got lastPage as null, so I added a check for it, https://business-logic-sro.sentry.io/issues/6333395682
      }

      const isFinalPage =
        lastPage.offset + lastPage.limit >= lastPage.totalCount;

      if (isFinalPage) {
        return undefined;
      }

      return { offset: lastPage.offset + lastPage.limit, limit: nextPageLimit };
    },
  });
